import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'react-use';
import { useBreakpoint } from 'app/spacing';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import { HistoryModal } from '__components/Modals/HistoryModal';
import { useColumns } from 'components/Modals/HistoryModal/hooks';
import BasicModal from '__components/Modals/presets/BasicModal';
import { StyledMetaData } from '__pages/SmartConnectors/SmartConnector/SmartConnectorSettings/subpages/TimelineTable/styles';
import { employeeHistoryQuery } from 'query/employee';
import { permissionGroupHistoryQuery } from 'query/permission-group';
import { roleHistoryQuery } from 'query/role';

type HistoryModalProps = {
  id: string;
  onHide(): void;
};

const DEFAULT_PAGINATION_SETTINGS = {
  page: 1,
  pageSize: 10,
  ordering: '-created',
};

const DetailsModal = ({ data, onHide }: any) => {
  const { t } = useTranslation();

  return (
    <BasicModal
      show
      onHide={onHide}
      onConfirm={onHide}
      heading={t('Timeline Details')}
      leftBtn={null}
      buttonText={t('Close')}
      actionBtnColor="blue"
      size="medium"
    >
      <StyledMetaData>{JSON.stringify(data || {}, null, 2)}</StyledMetaData>
    </BasicModal>
  );
};

export const EmployeeHistoryModal = ({ id, onHide }: HistoryModalProps) => {
  const { t } = useTranslation();
  const [excludeEventType, setExcludeEventType] = useState(['login']);
  const [history, setHistory] = useState<any>(null);
  const isMobile = useBreakpoint();
  const pagination = usePaginationSettings(DEFAULT_PAGINATION_SETTINGS);
  const [showModal, toggleModal] = useToggle(false);

  const { page, pageSize, ordering, search } = pagination;

  const queryParams = {
    page,
    page_size: pageSize,
    ordering,
    search,
    exclude_event_type: excludeEventType,
  };

  const historyQuery = useQuery(
    employeeHistoryQuery(id, {
      params: queryParams,
      placeholderData: keepPreviousData,
      staleTime: 0,
    })
  );

  const handleSelectAction = useCallback(
    ({ value }: any, data: any) => {
      if (value === 'view') {
        toggleModal();
        setHistory(data);
      }
    },
    [toggleModal]
  );

  const columns = useColumns(handleSelectAction);

  if (isMobile) {
    return null;
  }

  return (
    <>
      <HistoryModal
        show
        onHide={onHide}
        onConfirm={onHide}
        showEventTypeFilter
        heading={t('Team Member History Log')}
        loading={historyQuery.isLoading}
        results={historyQuery.data?.results ?? []}
        columns={columns}
        count={historyQuery.data?.count}
        pagination={pagination}
        showSearch
        setExcludeEventType={setExcludeEventType}
        excludeEventType={excludeEventType}
      />
      {showModal && <DetailsModal data={history.data} onHide={toggleModal} />}
    </>
  );
};

export const RoleHistoryModal = ({ id, onHide }: HistoryModalProps) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState<any>(null);
  const isMobile = useBreakpoint();
  const pagination = usePaginationSettings(DEFAULT_PAGINATION_SETTINGS);
  const [showModal, toggleModal] = useToggle(false);

  const { page, pageSize, ordering, search } = pagination;

  const queryParams = {
    page,
    page_size: pageSize,
    ordering,
    search,
  };

  const historyQuery = useQuery(
    roleHistoryQuery(id, {
      params: queryParams,
      placeholderData: keepPreviousData,
      staleTime: 0,
    })
  );

  const handleSelectAction = useCallback(
    ({ value }: any, data: any) => {
      if (value === 'view') {
        toggleModal();
        setHistory(data);
      }
    },
    [toggleModal]
  );

  const columns = useColumns(handleSelectAction);

  if (isMobile) {
    return null;
  }

  return (
    <>
      <HistoryModal
        show
        showSearch
        heading={t('Role History Log')}
        onHide={onHide}
        onConfirm={onHide}
        loading={historyQuery.isLoading}
        results={historyQuery.data?.results ?? []}
        columns={columns}
        count={historyQuery.data?.count}
        pagination={pagination}
      />
      {showModal && <DetailsModal data={history.data} onHide={toggleModal} />}
    </>
  );
};

export const PermissionGroupHistoryModal = ({
  id,
  onHide,
}: HistoryModalProps) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState<any>(null);
  const isMobile = useBreakpoint();
  const pagination = usePaginationSettings(DEFAULT_PAGINATION_SETTINGS);
  const [showModal, toggleModal] = useToggle(false);

  const { page, pageSize, ordering, search } = pagination;

  const queryParams = {
    page,
    page_size: pageSize,
    ordering,
    search,
  };

  const historyQuery = useQuery(
    permissionGroupHistoryQuery(id, {
      params: queryParams,
      placeholderData: keepPreviousData,
      staleTime: 0,
    })
  );

  const handleSelectAction = useCallback(
    ({ value }: any, data: any) => {
      if (value === 'view') {
        toggleModal();
        setHistory(data);
      }
    },
    [toggleModal]
  );

  const columns = useColumns(handleSelectAction);

  if (isMobile) {
    return null;
  }

  return (
    <>
      <HistoryModal
        show
        showSearch
        heading={t('Permission Group History Log')}
        onHide={onHide}
        onConfirm={onHide}
        loading={historyQuery.isLoading}
        results={historyQuery.data?.results ?? []}
        columns={columns}
        count={historyQuery.data?.count}
        pagination={pagination}
      />
      {showModal && <DetailsModal data={history.data} onHide={toggleModal} />}
    </>
  );
};
